import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, History, MailCheck, RotateCcw, Search, SlidersHorizontal, Workflow } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackPayload = {
  rules: [
    { _id: "preview-1", name: "Order payment thank-you email", ifTrigger: "order_paid", thenAction: "email_customer_invoice", owner: "Support", category: "emails", active: true },
    { _id: "preview-2", name: "Shipping tracking email", ifTrigger: "order_shipped", thenAction: "email_customer_tracking", owner: "Support", category: "emails", active: true },
    { _id: "preview-3", name: "Delivered thank-you email", ifTrigger: "order_delivered", thenAction: "email_customer_delivery_thanks", owner: "Support", category: "emails", active: true },
    { _id: "preview-admin-order", name: "Admin new-order notification", ifTrigger: "order_paid", thenAction: "email_admin_new_order", owner: "Operations", category: "emails", active: true },
    { _id: "preview-4", name: "Return request policy email", ifTrigger: "return_request_submitted", thenAction: "email_customer_return_policy", owner: "Returns", category: "emails", active: true },
    { _id: "preview-5", name: "Return label email", ifTrigger: "return_label_created", thenAction: "email_customer_return_tracking", owner: "Returns", category: "emails", active: true },
    { _id: "preview-6", name: "Return received thank-you email", ifTrigger: "return_received", thenAction: "email_customer_return_received_thanks", owner: "Returns", category: "emails", active: true },
    { _id: "preview-7", name: "Low inventory admin email", ifTrigger: "stock_below_threshold", thenAction: "email_admin_low_stock", owner: "Inventory", category: "inventory", active: true },
    { _id: "preview-8", name: "Contact form auto reply", ifTrigger: "contact_form_submitted", thenAction: "email_customer_contact_received", owner: "Support", category: "emails", active: true },
    { _id: "preview-9", name: "Support form auto reply", ifTrigger: "support_form_submitted", thenAction: "email_customer_support_received", owner: "Support", category: "emails", active: true },
    { _id: "preview-review", name: "Product review request", ifTrigger: "order_delivered", thenAction: "email_customer_review_request", owner: "Support", category: "emails", active: true },
  ],
  runs: [],
  metrics: { activeRules: 11, runsToday: 0, needsReview: 0, failedRuns: 0 },
};

const ruleDescriptions = {
  order_paid: "Sends a thank-you and order summary after customer payment is captured.",
  order_shipped: "Sends carrier and tracking number after tracking is assigned.",
  order_delivered: "Sends a thank-you email when Shippo tracking reports delivered.",
  return_request_submitted: "Sends the 14-day/original-package return policy response after customer submits an RMA.",
  return_label_created: "Sends return label and tracking instructions to the customer.",
  return_received: "Sends confirmation after the returned item is received or refund flow starts.",
  stock_below_threshold: "Emails the admin when a product falls below its low-stock threshold.",
  contact_form_submitted: "Sends an instant received message after the contact form is submitted.",
  support_form_submitted: "Sends an instant received message after a support request is submitted.",
};

function statusTone(status) {
  if (status === "success") return "good";
  if (status === "failed") return "bad";
  if (status === "needs_review") return "warn";
  return "neutral";
}

function categoryTone(category) {
  if (category === "inventory") return "warn";
  if (category === "emails") return "good";
  return "info";
}

function isPreview(id) {
  return String(id || "").startsWith("preview");
}

function readable(value) {
  return String(value || "").replaceAll("_", " ");
}

function AutomationSwitch({ disabled, enabled, onChange }) {
  return (
    <button
      aria-pressed={enabled}
      className={`inline-flex h-8 w-14 items-center rounded-full p-1 transition ${enabled ? "bg-emerald-500" : "bg-gray-300"} ${disabled ? "cursor-not-allowed opacity-60" : "hover:opacity-90"}`}
      disabled={disabled}
      onClick={onChange}
      type="button"
    >
      <span className={`h-6 w-6 rounded-full bg-white shadow-sm transition ${enabled ? "translate-x-6" : "translate-x-0"}`} />
    </button>
  );
}

export default function Automation() {
  const [activeTab, setActiveTab] = useState("automations");
  const [ruleFilter, setRuleFilter] = useState("all");
  const [runStatus, setRunStatus] = useState("all");
  const [runRule, setRunRule] = useState("all");
  const [search, setSearch] = useState("");
  const { dismiss, toast, toasts } = useToast();

  const loadAutomation = useCallback(() => api.get("/automation"), []);
  const { data, error, loading, reload, setData } = useAdminResource(loadAutomation, fallbackPayload);
  const rules = data.rules || fallbackPayload.rules;
  const runs = data.runs || fallbackPayload.runs;
  const metrics = data.metrics || fallbackPayload.metrics;
  const email = data.email || { configured: false };

  useEffect(() => {
    if (error && !loading) toast(`Using preview automation data: ${error}`, "warning");
  }, [error, loading, toast]);

  const visibleRules = useMemo(() => {
    return rules.filter((rule) => {
      if (ruleFilter === "enabled" && !rule.active) return false;
      if (ruleFilter === "paused" && rule.active) return false;
      return [rule.name, rule.ifTrigger, rule.thenAction, rule.owner].join(" ").toLowerCase().includes(search.toLowerCase());
    });
  }, [ruleFilter, rules, search]);

  const ruleNames = useMemo(() => [...new Set(runs.map((run) => run.ruleName).filter(Boolean))], [runs]);

  const visibleRuns = useMemo(() => {
    return runs.filter((run) => {
      const statusMatches = runStatus === "all" || run.status === runStatus;
      const ruleMatches = runRule === "all" || run.ruleName === runRule;
      const textMatches = [run.ruleName, run.trigger, run.action, run.message].join(" ").toLowerCase().includes(search.toLowerCase());
      return statusMatches && ruleMatches && textMatches;
    });
  }, [runRule, runStatus, runs, search]);

  const runPagination = usePagination(visibleRuns, [runStatus, runRule, search], 12);

  const updateRule = async (rule) => {
    if (isPreview(rule._id)) return;
    try {
      const data = await api.patch(`/automation/rules/${rule._id}`, { active: !rule.active });
      setData((current) => ({
        ...current,
        rules: (current.rules || []).map((item) => item._id === data.rule._id ? data.rule : item),
      }));
      toast(`${data.rule.name} ${data.rule.active ? "enabled" : "paused"}`, "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const retryRun = async (run) => {
    try {
      await api.post(`/automation/runs/${run._id}/retry`, {});
      toast("Email retry completed", "success");
      reload();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const verifyEmail = async () => {
    try {
      const result = await api.post("/automation/email/verify", {});
      setData((current) => ({ ...current, email: result.email }));
      toast("SMTP connection verified", "success");
    } catch (err) {
      toast(err.message, "error");
      reload();
    }
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Automation</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Email Automation Controls</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            These are fixed business automations. Admins can pause or enable them, and review sent or failed runs.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", onClick: reload }]} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InsightCard title="Enabled" value={metrics.activeRules || 0} note="Automations currently running" tone="good" />
        <InsightCard title="Paused" value={Math.max(0, rules.length - Number(metrics.activeRules || 0))} note="Automations not sending" tone="neutral" />
        <InsightCard title="Runs Today" value={metrics.runsToday || 0} note="Emails attempted today" tone="info" />
        <InsightCard title="Failures" value={metrics.failedRuns || 0} note="SMTP or delivery issues" tone="warn" />
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-gray-400">SMTP delivery</p>
          <p className="mt-1 text-sm font-bold text-gray-900">{email.verified ? "Connection verified" : email.configured ? "Configured, not verified" : "Configuration incomplete"}</p>
          <p className="mt-1 text-xs font-semibold text-gray-500">{email.hostConfigured ? `${email.host || "SMTP host"} · port ${email.port}` : "Set SMTP host, credentials, and sender address."}</p>
          {!email.configured && email.missingFields?.length ? (
            <p className="mt-1 text-xs font-bold text-amber-700">Missing: {email.missingFields.join(", ")}</p>
          ) : null}
        </div>
        <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-950 px-4 py-3 text-xs font-black text-white hover:bg-gray-800" onClick={verifyEmail} type="button"><RotateCcw size={14} /> Verify connection</button>
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm">
        {[
          ["Automations", "automations", Workflow],
          ["Run History", "runs", History],
        ].map(([label, value, Icon]) => (
          <button
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-black ${activeTab === value ? "bg-[#4391d1] text-white" : "text-gray-600 hover:bg-gray-50"}`}
            key={value}
            onClick={() => setActiveTab(value)}
            type="button"
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <Search className="absolute left-4 top-3.5 text-gray-400" size={16} />
            <input
              className="w-full rounded-xl border border-gray-200 px-4 py-3 pl-11 text-sm font-semibold outline-none focus:border-[#4391d1]"
              onChange={(event) => setSearch(event.target.value)}
              placeholder={activeTab === "runs" ? "Search run, message, trigger..." : "Search automation, trigger, owner..."}
              value={search}
            />
          </div>

          {activeTab === "automations" ? (
            <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setRuleFilter(event.target.value)} value={ruleFilter}>
              <option value="all">All automations</option>
              <option value="enabled">Enabled only</option>
              <option value="paused">Paused only</option>
            </select>
          ) : (
            <>
              <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setRunStatus(event.target.value)} value={runStatus}>
                <option value="all">All statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="needs_review">Needs review</option>
              </select>
              <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setRunRule(event.target.value)} value={runRule}>
                <option value="all">All automations</option>
                {ruleNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </>
          )}
        </div>
      </section>

      {activeTab === "automations" && (
        <section className="grid gap-4 xl:grid-cols-2">
          {visibleRules.map((rule) => (
            <article className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm" key={rule._id}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill tone={categoryTone(rule.category)}>{rule.category || "email"}</StatusPill>
                    <StatusPill tone={rule.active ? "good" : "neutral"}>{rule.active ? "Enabled" : "Paused"}</StatusPill>
                  </div>
                  <h2 className="mt-3 text-lg font-black text-gray-900">{rule.name}</h2>
                  <p className="mt-2 text-sm font-semibold leading-6 text-gray-500">
                    {ruleDescriptions[rule.ifTrigger] || `If ${readable(rule.ifTrigger)}, then ${readable(rule.thenAction)}.`}
                  </p>
                </div>
                <AutomationSwitch disabled={isPreview(rule._id)} enabled={Boolean(rule.active)} onChange={() => updateRule(rule)} />
              </div>

              <div className="mt-5 grid gap-3 border-t border-gray-100 pt-4 sm:grid-cols-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-[11px] font-black uppercase text-gray-400">Trigger</p>
                  <p className="mt-1 text-sm font-black text-gray-800">{readable(rule.ifTrigger)}</p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-[11px] font-black uppercase text-gray-400">Action</p>
                  <p className="mt-1 text-sm font-black text-gray-800">{readable(rule.thenAction)}</p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-[11px] font-black uppercase text-gray-400">Owner</p>
                  <p className="mt-1 text-sm font-black text-gray-800">{rule.owner || "Operations"}</p>
                </div>
              </div>
            </article>
          ))}

          {!visibleRules.length && (
            <div className="rounded-2xl bg-white p-8 text-sm font-bold text-gray-500 shadow-sm xl:col-span-2">
              No automations match the current filter.
            </div>
          )}
        </section>
      )}

      {activeTab === "runs" && (
        <section className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-blue-50 p-2 text-[#4391d1]"><SlidersHorizontal size={18} /></span>
              <div>
                <h2 className="text-xl font-black text-gray-900">Run History</h2>
                <p className="mt-1 text-sm font-semibold text-gray-500">Latest email automation attempts, sorted newest first.</p>
              </div>
            </div>
            <StatusPill tone="info">{visibleRuns.length} shown</StatusPill>
          </div>

          <div className="grid gap-3">
            {runPagination.paginatedItems.length ? runPagination.paginatedItems.map((run) => {
              const Icon = run.status === "success" ? CheckCircle2 : run.status === "failed" ? AlertTriangle : Clock3;
              return (
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4" key={run._id}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex min-w-0 gap-3">
                      <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${run.status === "success" ? "bg-emerald-50 text-emerald-600" : run.status === "failed" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                        <Icon size={18} />
                      </span>
                      <div className="min-w-0">
                        <p className="font-black text-gray-900">{run.ruleName}</p>
                        <p className="mt-1 text-sm font-semibold text-gray-600">{run.message || "Automation run completed."}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-black uppercase text-gray-400">
                          <span>{readable(run.trigger)}</span>
                          <span>to</span>
                          <span>{readable(run.action)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
                      <StatusPill tone={statusTone(run.status)}>{readable(run.status)}</StatusPill>
                      <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-black text-gray-500">
                        <MailCheck size={13} /> {formatUsDateTime(run.createdAt)}
                      </span>
                      {run.status !== "success" && !isPreview(run._id) && <button className="inline-flex items-center gap-1 rounded-lg bg-white px-3 py-2 text-xs font-black text-gray-700 hover:bg-gray-100" onClick={() => retryRun(run)} type="button"><RotateCcw size={13} /> Retry</button>}
                    </div>
                  </div>
                </div>
              );
            }) : (
              <p className="rounded-xl bg-gray-50 p-4 text-sm font-bold text-gray-500">No automation runs match the current filters.</p>
            )}
          </div>

          <PaginationControls pagination={runPagination} />
        </section>
      )}
    </div>
  );
}
